import React, { useCallback, useMemo } from "react";
import _ from "lodash";
import { wizardProps, WizardPage } from "@xengage/gw-portals-wizard-react";
import {
  ViewModelForm,
  withViewModelService,
} from "@xengage/gw-portals-viewmodel-react";
import { useValidation } from "@xengage/gw-portals-validation-react";
import { EntityUtil } from "@xengage/gw-portals-util-js";
import { readViewModelValue } from "@xengage/gw-jutro-adapters-react";
import { PremierConstants } from "premier-constant-service";
import { useUpdateSubmissionServices, validationUtil } from "premier-capability-hooks";
import styles from '../../SpaAndWellnessStyles.module.scss'
import presentationMetadata from "./PropertyPage.metadata.json5";
import "./PropertyPage.messages";
import InformationComponent from "../../components/Information/InformationComponent";

function PropertyPage(props) {
  const {
    wizardData,
    updateWizardData,
    currentStep,
    callbackMap,
    viewModelService,
  } = props;
  const { pageKey } = currentStep;
  // eslint-disable-next-line security/detect-object-injection
  const metadata = presentationMetadata[pageKey];
  const { initialValidation, onValidate } = useValidation("PropertyPage");
  const { updateSubmission } = useUpdateSubmissionServices();
  const { lineName } = PremierConstants;

  const onNext = useCallback(async () => {
    const { productCode } = wizardData.value.baseData;
    const lobLineName = lineName[productCode];

    wizardData.value = await updateSubmission(
      lobLineName,
      "PropertyPage",
      wizardData
    );

    return wizardData;
  }, [lineName, updateSubmission, wizardData]);

  const onSave = useCallback(
      async () => {
          const subVMCopy = _.clone(wizardData)
          const productCode = _.get(subVMCopy.value, 'baseData.productCode');
          
          validationUtil.unSetIsValidationReqFlag(subVMCopy.lobData.spaAndWellLine.coverables.locRisks.value, ' ')

          const lobLineName = productCode ? lineName[productCode] : '';
          await updateSubmission(lobLineName, 'PropertyPage', subVMCopy);
                          
          return wizardData;
      },
      [lineName, wizardData, updateSubmission]
  );

  const addInformation = useCallback(async () => {
    
    const informationObj = {
      tempID: EntityUtil.nextId(),
    };

    const { _xCenter, _dtoName } =
      wizardData.lobData.spaAndWellLine.coverables.locRisks;

    const informationVM = viewModelService.create(
      informationObj,
      _xCenter,
      _dtoName
    );

    wizardData.lobData.spaAndWellLine.coverables.locRisks.value =
      wizardData.lobData.spaAndWellLine.coverables.locRisks.value || [];

    wizardData.lobData.spaAndWellLine.coverables.locRisks.pushElement(
      informationVM
    );
    updateWizardData(wizardData);
  }, [wizardData, viewModelService, updateWizardData]);

  const informationOverrides = useMemo(() => {
    const informationList = _.get(
      wizardData,
      "lobData.spaAndWellLine.coverables.locRisks",
      []
    );

     _.each(informationList.value, (info) => {
      _.set(info, 'isValidationReq', true)
      if(wizardData.value.lobData.spaAndWellLine.coverables.beauticiansSpa !== undefined) {
        _.set(info, 'criFie', wizardData.value.lobData.spaAndWellLine.coverables.beauticiansSpa.productsandCoverageSelection.criFie) 
      } else if(wizardData.value.lobData.spaAndWellLine.coverables.healthWellness !== undefined) {
        _.set(info, 'criFie', wizardData.value.lobData.spaAndWellLine.coverables.healthWellness.productsCoverageSelection.criFie) 
      }
    });

    let overrides = [];

    if (!_.isEmpty(informationList)) {
      overrides = informationList?.children.map((information, index) => ({
        [`informationComponent${index}`]: {
          overrideProps: {
            "@field": {
              showRequired: true,
              labelPosition: "left",
            },
          },
          submissionVM: wizardData,
          updateWizardData,
          pathUpdated: `lobData.spaAndWellLine.coverables.locRisks.children[${index}]`,
          informationVm: information,
          index,
          viewModelService,
        },
      }));
    }

    return Object.assign({}, ...overrides);
  }, [updateWizardData, viewModelService, wizardData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const overrideProps = {
    ...informationOverrides,
  };

  const resolveCallbackMap = {
    addInformation,
    ...callbackMap,
  };

  const readValue = useCallback(
    (id, path) =>
      readViewModelValue(metadata.content, wizardData, id, path, overrideProps),
    [metadata.content, wizardData, overrideProps]
  );

  const resolveComponentMap = {
    informationComponent: InformationComponent,
  };

  const isNeighboruingOccDisabled = () => {
    const fields = ['busFie', 'comFie', 'hosFie', 'indFie', 'offFie', 'resFie', 'othFie']
    return wizardData.value.lobData.spaAndWellLine.coverables.locRisks.every(loc =>
      fields.some(field => loc[field] === true)
    )
  }

  const isDisableNext = useCallback(() =>
    wizardData.value.lobData.spaAndWellLine.coverables.locRisks.length === 0
    || (wizardData.value.lobData.spaAndWellLine.coverables.locRisks.length > 0 &&
      !_.get(wizardData, 'lobData.spaAndWellLine.coverables.locRisks.aspects.subtreeValid'))
      || !isNeighboruingOccDisabled()
    , [wizardData])

  return (
    <WizardPage
      skipWhen={() => initialValidation}
      disableNext={isDisableNext()}
      onNext={onNext}
      onSave={onSave}
    >
      <ViewModelForm
        uiProps={metadata.content}
        model={wizardData}
        onModelChange={updateWizardData}
        callbackMap={resolveCallbackMap}
        resolveValue={readValue}
        overrideProps={overrideProps}
        onValidationChange={onValidate}
        componentMap={resolveComponentMap}
        classNameMap={styles}
      />
    </WizardPage>
  );
}

PropertyPage.propTypes = wizardProps;
export default withViewModelService(PropertyPage);
